import type { Block } from "@/types/block";
import type { DatabaseSource } from "@/types/board";
import { isDatasourceDead } from "@/lib/datasourceTombstones";
import {
  buildFormulaDefinitionsFromSchema,
  type FormulaPropertyDefinition,
  type RelatedRowContext,
  type RelationResolver,
} from "./evaluator";

/**
 * Build the RelationResolver the formula engine's aggregation functions
 * (relatedCount / countIf / sumBy / someBy / everyBy / joinBy) call to turn a
 * relation cell into related rows + their datasource schema.
 *
 * Purely synchronous over data the board context already holds — the same
 * reach rollups use (rollupUtils.computeRollupData). Returns null when the
 * related datasource is not hydrated yet, which the engine surfaces as a
 * clear "not loaded" error instead of a silently-wrong 0.
 *
 * One resolver instance is built per evaluation pass (per useMemo run), so
 * the per-datasource row map and schema are cached on the closure: resolving
 * N rows × M relation ids stays O(ids) instead of O(ids × relatedRows).
 */
/** On-demand hydration: report an unresolvable linked datasource so the caller
 *  can fetch it; isFetched stops the error once a fetch has been tried. */
export interface RelationResolverHydration {
  onMissing?: (linkedDatabaseId: string) => void;
  isFetched?: (linkedDatabaseId: string) => boolean;
}

export function createBoardRelationResolver(
  properties: Record<string, any>,
  getNotesByDataSourceId: (dataSourceId: string) => Block[],
  getDataSource: (dataSourceId: string) => DatabaseSource | undefined,
  hydration?: RelationResolverHydration
): RelationResolver {
  const rowMaps = new Map<string, Map<string, Block>>();
  const schemas = new Map<string, Record<string, FormulaPropertyDefinition>>();

  const rowsFor = (dsId: string): Map<string, Block> => {
    let map = rowMaps.get(dsId);
    if (!map) {
      map = new Map(
        getNotesByDataSourceId(dsId).map((n) => [String(n._id), n])
      );
      rowMaps.set(dsId, map);
    }
    return map;
  };

  const schemaFor = (
    dsId: string
  ): Record<string, FormulaPropertyDefinition> | null => {
    const cached = schemas.get(dsId);
    if (cached) return cached;
    const ds = getDataSource(dsId);
    if (!ds) return null;
    const defs = buildFormulaDefinitionsFromSchema(
      (ds.properties ?? {}) as Record<string, any>
    );
    schemas.set(dsId, defs);
    return defs;
  };

  return (
    relationPropertyId: string,
    relatedIds: string[],
    linkedDatabaseId?: string
  ) => {
    // The engine passes the target datasource of the relation property being
    // resolved, taken from the schema of the row currently being evaluated —
    // so nested aggregation (a related row's own relation formula) resolves
    // against the right datasource. Fall back to the root board schema for
    // the top-level case / older callers that don't pass it.
    const linkedDbId = linkedDatabaseId
      ? String(linkedDatabaseId)
      : properties?.[relationPropertyId]?.linkedDatabaseId
        ? String(properties[relationPropertyId].linkedDatabaseId)
        : "";
    if (!linkedDbId) return null;

    // Deleted datasource: there are genuinely no related rows, and fetching it
    // would 404. Returning null here would error "not loaded yet" forever.
    if (isDatasourceDead(linkedDbId)) return [];

    const schema = schemaFor(linkedDbId);
    if (!schema) {
      // Schema not hydrated yet — ask the caller to fetch it, then error.
      hydration?.onMissing?.(linkedDbId);
      return null;
    }

    const rows = rowsFor(linkedDbId);
    // Datasource claims rows (blockIds) but none are loaded → fetch, and error
    // until it lands. Requiring blockIds leaves a genuinely empty table at 0.
    const expectedRows = getDataSource(linkedDbId)?.blockIds?.length ?? 0;
    if (
      hydration?.onMissing &&
      rows.size === 0 &&
      expectedRows > 0 &&
      !hydration.isFetched?.(linkedDbId)
    ) {
      hydration.onMissing(linkedDbId);
      return null;
    }

    const out: RelatedRowContext[] = [];
    for (const id of relatedIds) {
      const note = rows.get(String(id));
      if (!note) continue; // stale/archived relation entries are skipped
      out.push({
        note: {
          _id: String(note._id),
          title: (note.value as any)?.title ?? "",
          databaseProperties: (note.value as any)?.databaseProperties ?? {},
        },
        properties: schema,
      });
    }
    return out;
  };
}
